import React, { useState } from 'react';
import styles from '../../styles/LoginRegister.module.css';
import buttonStyles from '../../styles/Buttons.module.css';
import type { UserRegistrationDetails } from '../../types/Registration';
import PasswordToggleButton from './PasswordToggleButton';

export interface UserRegistrationFormProps {
  // Data and state management
  initialData?: Partial<UserRegistrationDetails>;
  
  // Form behavior
  mode: 'signup' | 'invitation';
  emailReadonly?: boolean;
  
  // Event handlers
  onSubmit: (userDetails: UserRegistrationDetails) => void;
  onBack?: () => void;
  
  // UI customization
  submitButtonText: string;
  title: string;
  description: string;
  
  // Loading and error states
  isLoading?: boolean;
  error?: string | null;
  formErrors?: Record<string, string>;
}

const UserRegistrationForm: React.FC<UserRegistrationFormProps> = ({
  initialData = {},
  mode,
  emailReadonly = false,
  onSubmit,
  onBack,
  submitButtonText,
  title,
  description,
  isLoading = false,
  error = null,
  formErrors = {}
}) => {
  const [userDetails, setUserDetails] = useState<UserRegistrationDetails>({
    // Core Identity
    surname: initialData.surname || '',
    fullNames: initialData.fullNames || '',
    nickname: initialData.nickname || '',
    email: initialData.email || '',
    password: initialData.password || '',
    confirmPassword: initialData.confirmPassword || '',
    idNumber: initialData.idNumber || '',
    identificationType: initialData.identificationType || 'RSA ID',
    dateOfBirth: initialData.dateOfBirth || '',
    gender: initialData.gender || 'Male',
    
    // Contact
    mobileNumber: initialData.mobileNumber || '',
    homeTelephone: initialData.homeTelephone || '',
    
    // Physical Address
    unitNumber: initialData.unitNumber || '',
    complex: initialData.complex || '',
    streetNumber: initialData.streetNumber || '',
    streetName: initialData.streetName || '',
    suburb: initialData.suburb || '',
    city: initialData.city || '',
    postalCode: initialData.postalCode || '',
    
    // Postal Address (new shape)
    postalAddressSameAsResidential: initialData.postalAddressSameAsResidential !== false,
    postalUnitNumber: initialData.postalUnitNumber || '',
    postalComplex: initialData.postalComplex || '',
    postalStreetNumber: initialData.postalStreetNumber || '',
    postalStreetName: initialData.postalStreetName || '',
    postalSuburb: initialData.postalSuburb || '',
    postalCity: initialData.postalCity || '',
    postalPostalCode: initialData.postalPostalCode || '',
    // Legal Agreements
    agreedTerms: initialData.agreedTerms || false,
    agreedPOPIA: initialData.agreedPOPIA || false,
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>(formErrors);

  // Password validation helpers
  const hasMinLength = (password: string) => password.length >= 8;
  const hasNumber = (password: string) => /\d/.test(password);
  const hasSpecialChar = (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const passwordsMatch = () => userDetails.password && userDetails.password === userDetails.confirmPassword;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setUserDetails((prev: UserRegistrationDetails) => ({ ...prev, [name]: checked }));
    } else {
      // Strip whitespace from password fields
      let finalValue = (name === 'password' || name === 'confirmPassword')
        ? value.replace(/\s/g, '')
        : value;
      // Normalize mobile number to E.164 as the user types
      if (name === 'mobileNumber') {
        const digits = value.replace(/[^\d+]/g, '');
        if (digits.startsWith('+')) {
          finalValue = digits;
        } else {
          // Strip leading 0 and prepend +27 for SA local format
          finalValue = '+27' + digits.replace(/^0/, '');
        }
      }
      setUserDetails((prev: UserRegistrationDetails) => ({ ...prev, [name]: finalValue }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Core validations
    if (!userDetails.surname?.trim()) {
      newErrors.surname = 'Surname is required';
    } else if (userDetails.surname.length < 2) {
      newErrors.surname = 'Surname must be at least 2 characters';
    }

    if (!userDetails.fullNames?.trim()) {
      newErrors.fullNames = 'Full names are required';
    } else if (userDetails.fullNames.length < 2) {
      newErrors.fullNames = 'Full names must be at least 2 characters';
    }

    // Email validation (only for signup mode)
    if (mode === 'signup') {
      if (!userDetails.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(userDetails.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    // Password validation
    if (!userDetails.password) {
      newErrors.password = 'Password is required';
    } else if (/\s/.test(userDetails.password)) {
      newErrors.password = 'Password cannot contain spaces';
    } else if (userDetails.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!hasNumber(userDetails.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!hasSpecialChar(userDetails.password)) {
      newErrors.password = 'Password must contain at least one special character';
    }

    if (!userDetails.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (/\s/.test(userDetails.confirmPassword)) {
      newErrors.confirmPassword = 'Password cannot contain spaces';
    } else if (userDetails.password !== userDetails.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // ID validation based on type
    if (!userDetails.idNumber?.trim()) {
      newErrors.idNumber = 'ID/Passport number is required';
    } else {
      if (userDetails.identificationType === 'RSA ID' && !/^\d{13}$/.test(userDetails.idNumber)) {
        newErrors.idNumber = 'Please enter a valid 13-digit RSA ID number';
      }
    }

    // Demographics
    if (!userDetails.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!userDetails.gender) newErrors.gender = 'Gender is required';
    
    // Contact validations
    if (!userDetails.mobileNumber?.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\+\d{10,15}$/.test(userDetails.mobileNumber)) {
      newErrors.mobileNumber = 'Enter a valid mobile number (e.g. 0821234567 or +27821234567)';
    }
    
    // Address validations
    if (!userDetails.streetNumber?.trim()) newErrors.streetNumber = 'Street number is required';
    if (!userDetails.streetName?.trim()) newErrors.streetName = 'Street name is required';
    if (!userDetails.suburb?.trim()) newErrors.suburb = 'Suburb/District is required';
    if (!userDetails.city?.trim()) newErrors.city = 'City/Town is required';
    
    if (!userDetails.postalCode?.trim()) {
      newErrors.postalCode = 'Postal code is required';
    } else if (!/^\d{4}$/.test(userDetails.postalCode)) {
      newErrors.postalCode = 'Please enter a valid 4-digit postal code';
    }

    // Postal Address validation (only when postal differs from residential)
    if (userDetails.postalAddressSameAsResidential === false) {
      if (!userDetails.postalStreetNumber?.trim()) newErrors.postalStreetNumber = 'Postal street number is required';
      if (!userDetails.postalStreetName?.trim()) newErrors.postalStreetName = 'Postal street name is required';
      if (!userDetails.postalSuburb?.trim()) newErrors.postalSuburb = 'Postal suburb is required';
      if (!userDetails.postalCity?.trim()) newErrors.postalCity = 'Postal city is required';
      if (!userDetails.postalPostalCode?.trim()) {
        newErrors.postalPostalCode = 'Postal code is required';
      } else if (!/^\d{4}$/.test(userDetails.postalPostalCode)) {
        newErrors.postalPostalCode = 'Please enter a valid 4-digit postal code';
      }
    }

    // Legal agreements validation
    if (!userDetails.agreedTerms) {
      newErrors.agreedTerms = 'You must agree to the Terms and Conditions';
    }
    if (!userDetails.agreedPOPIA) {
      newErrors.agreedPOPIA = 'You must consent to the POPIA Privacy Notice';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    onSubmit(userDetails);
  };

  return (
    <div className={styles.formSection}>
      <h2 className={styles.formTitle}>{title}</h2>
      <p className={styles.formDescription}>
        {description}
      </p>
      
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* CORE IDENTITY SECTION */}
        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Identity Details</h3>
          
          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Full Names *</label>
            <input
              type="text"
              name="fullNames"
              value={userDetails.fullNames}
              onChange={handleChange}
              placeholder="Enter your full names"
              className={styles.fieldInput}
              required
            />
            {errors.fullNames && <div className={styles.errorMessage}>{errors.fullNames}</div>}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Surname *</label>
            <input
              type="text"
              name="surname"
              value={userDetails.surname}
              onChange={handleChange}
              placeholder="Enter your surname"
              className={styles.fieldInput}
              required
            />
            {errors.surname && <div className={styles.errorMessage}>{errors.surname}</div>}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Nickname (Preferred Name)</label>
            <input
              type="text"
              name="nickname"
              value={userDetails.nickname || ''}
              onChange={handleChange}
              placeholder="Enter your preferred name (optional)"
              className={styles.fieldInput}
            />
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Date of Birth *</label>
            <input
              type="date"
              name="dateOfBirth"
              value={userDetails.dateOfBirth}
              onChange={handleChange}
              className={styles.fieldInput}
              required
            />
            {errors.dateOfBirth && <div className={styles.errorMessage}>{errors.dateOfBirth}</div>}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Gender *</label>
            <select
              name="gender"
              value={userDetails.gender}
              onChange={handleChange}
              className={styles.fieldSelect}
              required
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            {errors.gender && <div className={styles.errorMessage}>{errors.gender}</div>}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Identification Type *</label>
            <select
              name="identificationType"
              value={userDetails.identificationType}
              onChange={handleChange}
              className={styles.fieldSelect}
              required
            >
              <option value="RSA ID">RSA ID</option>
              <option value="Passport">Passport</option>
              <option value="Foreign ID">Foreign ID</option>
            </select>
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>ID/Passport Number *</label>
            <input
              type="text"
              name="idNumber"
              value={userDetails.idNumber}
              onChange={handleChange}
              placeholder={userDetails.identificationType === 'RSA ID' ? 'Enter your 13-digit ID number' : 'Enter your ID/Passport number'}
              className={styles.fieldInput}
              required
            />
            {errors.idNumber && <div className={styles.errorMessage}>{errors.idNumber}</div>}
          </div>
        </div>

        {/* CONTACT SECTION */}
        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Contact Details</h3>
          
          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Email Address {mode === 'signup' ? '*' : ''}</label>
            <input
              type="email"
              name="email"
              value={userDetails.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              className={styles.fieldInput}
              disabled={emailReadonly}
              required={mode === 'signup'}
            />
            {errors.email && <div className={styles.errorMessage}>{errors.email}</div>}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Mobile Number *</label>
            <input
              type="tel"
              name="mobileNumber"
              value={userDetails.mobileNumber}
              onChange={handleChange}
              placeholder="e.g. 0821234567 or +27821234567"
              className={styles.fieldInput}
              required
            />
            {errors.mobileNumber && <div className={styles.errorMessage}>{errors.mobileNumber}</div>}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Home Telephone (Optional)</label>
            <input
              type="tel"
              name="homeTelephone"
              value={userDetails.homeTelephone || ''}
              onChange={handleChange}
              placeholder="Enter your home telephone (optional)"
              className={styles.fieldInput}
            />
          </div>
        </div>

        {/* PHYSICAL ADDRESS SECTION */}
        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Residential Address</h3>
          
          <div className={styles.fieldRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Unit Number (Optional)</label>
              <input
                type="text"
                name="unitNumber"
                value={userDetails.unitNumber || ''}
                onChange={handleChange}
                placeholder="Unit #"
                className={styles.fieldInput}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Complex Name (Optional)</label>
              <input
                type="text"
                name="complex"
                value={userDetails.complex || ''}
                onChange={handleChange}
                placeholder="Complex name"
                className={styles.fieldInput}
              />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Street Number *</label>
              <input
                type="text"
                name="streetNumber"
                value={userDetails.streetNumber}
                onChange={handleChange}
                placeholder="123"
                className={styles.fieldInput}
                required
              />
              {errors.streetNumber && <div className={styles.errorMessage}>{errors.streetNumber}</div>}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Street Name *</label>
              <input
                type="text"
                name="streetName"
                value={userDetails.streetName}
                onChange={handleChange}
                placeholder="Main Street"
                className={styles.fieldInput}
                required
              />
              {errors.streetName && <div className={styles.errorMessage}>{errors.streetName}</div>}
            </div>
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Suburb/District *</label>
            <input
              type="text"
              name="suburb"
              value={userDetails.suburb}
              onChange={handleChange}
              placeholder="Enter suburb or district"
              className={styles.fieldInput}
              required
            />
            {errors.suburb && <div className={styles.errorMessage}>{errors.suburb}</div>}
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>City/Town *</label>
              <input
                type="text"
                name="city"
                value={userDetails.city}
                onChange={handleChange}
                placeholder="Enter city or town"
                className={styles.fieldInput}
                required
              />
              {errors.city && <div className={styles.errorMessage}>{errors.city}</div>}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Postal Code *</label>
              <input
                type="text"
                name="postalCode"
                value={userDetails.postalCode}
                onChange={handleChange}
                placeholder="0000"
                maxLength={4}
                className={styles.fieldInput}
                required
              />
              {errors.postalCode && <div className={styles.errorMessage}>{errors.postalCode}</div>}
            </div>
          </div>
        </div>

        {/* POSTAL ADDRESS SECTION */}
        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Postal Address</h3>

          <div className={styles.checkboxRow}>
            <input
              type="checkbox"
              name="postalAddressSameAsResidential"
              checked={userDetails.postalAddressSameAsResidential !== false}
              onChange={() => {
                const same = !userDetails.postalAddressSameAsResidential;
                if (same) {
                  setUserDetails((prev: UserRegistrationDetails) => ({
                    ...prev,
                    postalAddressSameAsResidential: true,
                    postalUnitNumber: prev.unitNumber || '',
                    postalComplex: prev.complex || '',
                    postalStreetNumber: prev.streetNumber || '',
                    postalStreetName: prev.streetName || '',
                    postalSuburb: prev.suburb || '',
                    postalCity: prev.city || '',
                    postalPostalCode: prev.postalCode || ''
                  }));
                } else {
                  setUserDetails((prev: UserRegistrationDetails) => ({ ...prev, postalAddressSameAsResidential: false }));
                }
              }}
              id="sameAsResidential"
            />
            <label htmlFor="sameAsResidential" className={styles.checkboxLabel}>
              Same as residential address
            </label>
          </div>

          {userDetails.postalAddressSameAsResidential === false && (
            <>
              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Unit Number (Optional)</label>
                  <input
                    type="text"
                    name="postalUnitNumber"
                    value={userDetails.postalUnitNumber || ''}
                    onChange={handleChange}
                    placeholder="Unit #"
                    className={styles.fieldInput}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Complex Name (Optional)</label>
                  <input
                    type="text"
                    name="postalComplex"
                    value={userDetails.postalComplex || ''}
                    onChange={handleChange}
                    placeholder="Complex name"
                    className={styles.fieldInput}
                  />
                </div>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Street Number *</label>
                  <input
                    type="text"
                    name="postalStreetNumber"
                    value={userDetails.postalStreetNumber}
                    onChange={handleChange}
                    placeholder="123"
                    className={styles.fieldInput}
                    required
                  />
                  {errors.postalStreetNumber && <div className={styles.errorMessage}>{errors.postalStreetNumber}</div>}
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Street Name *</label>
                  <input
                    type="text"
                    name="postalStreetName"
                    value={userDetails.postalStreetName}
                    onChange={handleChange}
                    placeholder="Main Street"
                    className={styles.fieldInput}
                    required
                  />
                  {errors.postalStreetName && <div className={styles.errorMessage}>{errors.postalStreetName}</div>}
                </div>
              </div>

              <div className={styles.fieldContainer}>
                <label className={styles.fieldLabel}>Suburb/District *</label>
                <input
                  type="text"
                  name="postalSuburb"
                  value={userDetails.postalSuburb}
                  onChange={handleChange}
                  placeholder="Enter suburb or district"
                  className={styles.fieldInput}
                  required
                />
                {errors.postalSuburb && <div className={styles.errorMessage}>{errors.postalSuburb}</div>}
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>City/Town *</label>
                  <input
                    type="text"
                    name="postalCity"
                    value={userDetails.postalCity}
                    onChange={handleChange}
                    placeholder="Enter city or town"
                    className={styles.fieldInput}
                    required
                  />
                  {errors.postalCity && <div className={styles.errorMessage}>{errors.postalCity}</div>}
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Postal Code *</label>
                  <input
                    type="text"
                    name="postalPostalCode"
                    value={userDetails.postalPostalCode || ''}
                    onChange={handleChange}
                    placeholder="0000"
                    maxLength={4}
                    className={styles.fieldInput}
                    required
                  />
                  {errors.postalPostalCode && <div className={styles.errorMessage}>{errors.postalPostalCode}</div>}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Postal City field (when postal address not same as residential) */}
        {userDetails.postalAddressSameAsResidential === false && (
          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Postal City *</label>
            <input
              type="text"
              name="postalCity"
              value={userDetails.postalCity || ''}
              onChange={handleChange}
              placeholder="City or town"
              className={styles.fieldInput}
              required={!userDetails.postalAddressSameAsResidential}
            />
            {errors.postalCity && <div className={styles.errorMessage}>{errors.postalCity}</div>}
          </div>
        )}

        {/* PASSWORD SECTION */}
        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Account Security</h3>
          
          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Password *</label>
            <div className={styles.passwordFieldWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={userDetails.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className={styles.fieldInput}
                style={{ fontFamily: 'monospace' }}
                required
              />
              <PasswordToggleButton
                visible={showPassword}
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>
            {errors.password && <div className={styles.errorMessage}>{errors.password}</div>}
            
            {/* Password Requirements Indicator */}
            {userDetails.password && (
              <div className={styles.passwordRequirements}>
                <div className={`${styles.requirementItem} ${hasMinLength(userDetails.password) ? styles.met : styles.unmet}`}>
                  <span className={styles.requirementIcon}>{hasMinLength(userDetails.password) ? '✓' : '○'}</span>
                  <span>At least 8 characters</span>
                </div>
                <div className={`${styles.requirementItem} ${hasNumber(userDetails.password) ? styles.met : styles.unmet}`}>
                  <span className={styles.requirementIcon}>{hasNumber(userDetails.password) ? '✓' : '○'}</span>
                  <span>Contains a number</span>
                </div>
                <div className={`${styles.requirementItem} ${hasSpecialChar(userDetails.password) ? styles.met : styles.unmet}`}>
                  <span className={styles.requirementIcon}>{hasSpecialChar(userDetails.password) ? '✓' : '○'}</span>
                  <span>Contains a special character (!@#$%^&* etc.)</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Confirm Password *</label>
            <div className={styles.passwordFieldWrapper}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={userDetails.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className={styles.fieldInput}
                style={{ fontFamily: 'monospace' }}
                required
              />
              <PasswordToggleButton
                visible={showConfirmPassword}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              />
            </div>
            {errors.confirmPassword && <div className={styles.errorMessage}>{errors.confirmPassword}</div>}

            {/* Password Match Indicator */}
            {userDetails.confirmPassword && (
              <div className={`${styles.passwordMatchIndicator} ${
                passwordsMatch() ? styles.match : styles.noMatch
              }`}>
                <span className={styles.requirementIcon}>{passwordsMatch() ? '✓' : '✗'}</span>
                <span>{passwordsMatch() ? 'Passwords match' : 'Passwords do not match'}</span>
              </div>
            )}
          </div>
        </div>

        {/* LEGAL AGREEMENTS SECTION */}
        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Required Agreements</h3>
          <p className={styles.formDescription}>
            Please review and accept the following agreements to proceed with registration:
          </p>
          
          <label className={styles.termsAgreementLabel}>
            <input
              type="checkbox"
              name="agreedTerms"
              checked={userDetails.agreedTerms}
              onChange={handleChange}
              className={styles.termsCheckbox}
            />
            <span className={styles.termsAgreementText}>
              I have read and agree to the{' '}
              <a 
                href="/TermsAndConditions" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.termsLink}
              >
                Terms and Conditions
              </a>{' '}*
            </span>
          </label>
          {errors.agreedTerms && <div className={styles.errorMessage}>{errors.agreedTerms}</div>}

          <label className={styles.termsAgreementLabel}>
            <input
              type="checkbox"
              name="agreedPOPIA"
              checked={userDetails.agreedPOPIA}
              onChange={handleChange}
              className={styles.termsCheckbox}
            />
            <span className={styles.termsAgreementText}>
              I consent to the collection and processing of my personal information as described in the{' '}
              <a 
                href="/POPIAConsent" 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.termsLink}
              >
                POPIA Privacy Notice
              </a>{' '}*
            </span>
          </label>
          {errors.agreedPOPIA && <div className={styles.errorMessage}>{errors.agreedPOPIA}</div>}
        </div>

        <div className={styles.buttonGroupSpaced}>
          {onBack && (
            <button type="button" onClick={onBack} className={`${buttonStyles.button} ${buttonStyles.formSecondary}`}>
              Back
            </button>
          )}
          <button 
            type="submit" 
            className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}
            disabled={isLoading || !userDetails.agreedTerms || !userDetails.agreedPOPIA}
            title={!userDetails.agreedTerms || !userDetails.agreedPOPIA ? 'Please accept all agreements to proceed' : ''}
          >
            {isLoading ? 'Processing...' : submitButtonText}
          </button>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}
        {errors.form && <div className={styles.errorMessage}>{errors.form}</div>}
      </form>
    </div>
  );
};

export default UserRegistrationForm;